<template>
  <div>
    <v-container grid-list-md class="relatorios-pagamentos-container">
      <v-layout row wrap class="px-5 pb-5 pt-5">
        <v-flex xs12>
          <v-card class="px-4 py-3">
            <div class="subheading"><strong>Filtros</strong></div>
            <v-layout row wrap class="mt-2">
              <v-flex xs12 md4>
                <v-select
                  :items="periodoItems"
                  item-text="text"
                  item-value="value"
                  v-model="periodo"
                  label="Período"
                  single-line
                ></v-select>
              </v-flex>
              <v-flex xs12 md4 v-if="periodo === 'custom'">
                <v-menu full-width offset-y :close-on-content-click="false" v-model="dateMenu" bottom>
                  <v-text-field
                    slot="activator"
                    :value="`De ${formatDate(dataInicial)} até ${formatDate(dataFinal)}`"
                    label="Período customizado"
                    readonly
                  ></v-text-field>
                  <v-card>
                    <v-card-text>
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-text-field
                            v-model="dataInicial"
                            type="date"
                            label="Data inicial"
                            class="premium-input"
                            @input="onCustomDateInput"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                          <v-text-field
                            v-model="dataFinal"
                            type="date"
                            label="Data final"
                            class="premium-input"
                            @input="onCustomDateInput"
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 class="text-xs-right">
                          <v-btn flat color="grey darken-1" @click="dateMenu = false">Fechar</v-btn>
                          <v-btn color="secondary" dark :disabled="!isCustomDateRangeValid" @click="applyCustomDateRange">Aplicar</v-btn>
                        </v-flex>
                      </v-layout>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>
              <v-flex xs12 md4>
                <v-select
                  class="filtro-imovel"
                  :items="imovelItems"
                  item-text="text"
                  item-value="value"
                  v-model="selectedImovelId"
                  @input="onImovelInput"
                  @change="onImovelInput"
                  label="Imóvel"
                  autocomplete
                  no-data-text="Nenhum imóvel encontrado"
                  :filter="imovelFilter"
                  clearable
                  :loading="imoveisLoading"
                >
                  <template slot="selection" scope="data">
                    <span class="filtro-imovel-selecao-title">{{ data.item.text }}</span>
                  </template>
                  <template slot="item" scope="data">
                    <v-list-tile-content class="filtro-imovel-opcao-wrap">
                      <v-list-tile-title class="filtro-imovel-opcao-title">
                        {{ data.item.text }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </template>
                </v-select>
              </v-flex>
              <v-flex xs12 md4>
                <v-select
                  :items="statusItems"
                  v-model="status"
                  label="Status"
                  multiple
                  chips
                  clearable
                ></v-select>
              </v-flex>
              <v-flex xs12 class="text-xs-right mt-2">
                <v-btn class="grey" dark @click.native="download('pdf')" :disabled="itens.length < 1">PDF</v-btn>
                <v-btn class="grey" dark @click.native="download('excel')" :disabled="itens.length < 1">Excel</v-btn>
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>

        <v-flex xs12 sm4>
          <v-card class="pa-4 resumo-card">
            <div class="caption grey--text">Período aplicado</div>
            <div class="subheading"><strong>{{ periodoTitulo }}</strong></div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4>
          <v-card class="pa-4 resumo-card">
            <div class="caption grey--text">Total de despesas</div>
            <div class="headline"><strong>{{ resumo.total || 0 }}</strong></div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4>
          <v-card class="pa-4 resumo-card">
            <div class="caption grey--text">Valor total</div>
            <div class="headline"><strong>{{ formatCurrency(resumo.valor_total || 0) }}</strong></div>
          </v-card>
        </v-flex>

        <v-flex xs12>
          <v-card class="pa-4">
            <div class="subheading mb-3"><strong>Resumo por status</strong></div>
            <div v-if="statusRows.length < 1" class="caption grey--text">Sem dados para o período selecionado.</div>
            <table v-else class="resumo-status-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in statusRows" :key="item.status">
                  <td>{{ item.status }}</td>
                  <td>{{ item.total }}</td>
                  <td>{{ formatCurrency(item.valor_total) }}</td>
                </tr>
              </tbody>
            </table>
          </v-card>
        </v-flex>

        <v-flex xs12>
          <v-card class="pa-4">
            <div class="subheading mb-3"><strong>Itens</strong></div>
            <div v-if="itens.length < 1" class="caption grey--text">Nenhum pagamento encontrado para os filtros aplicados.</div>
            <div class="table-responsive" v-else>
              <table class="itens-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data ref.</th>
                    <th>Vencimento</th>
                    <th>Pagamento</th>
                    <th>Status</th>
                    <th>Descrição</th>
                    <th>Imóvel</th>
                    <th>Solicitante</th>
                    <th>Aprovador</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in itens" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ formatDate(item.data_referencia) }}</td>
                    <td>{{ formatDate(item.data_vencimento) }}</td>
                    <td>{{ formatDate(item.data_pagamento) }}</td>
                    <td>{{ formatStatus(item.status) }}</td>
                    <td>{{ item.descricao || '-' }}</td>
                    <td>{{ item.imovel ? item.imovel.apelido : '-' }}</td>
                    <td>{{ item.solicitante ? item.solicitante.name : '-' }}</td>
                    <td>{{ item.aprovador ? item.aprovador.name : '-' }}</td>
                    <td>{{ formatCurrency(item.valor) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
  import _ from 'lodash'
  import moment from 'moment'
  import currencyFormatter from 'currency-formatter'
  import * as jsPDF from 'jspdf'
  import 'jspdf-autotable'
  import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
  import { buildRelatorioPagamentosPayload, normalizeImovelFilterValue, includesNormalizedText, normalizeSearchText } from '@/utils/relatoriosPagamentos'

  export default {
    data () {
      return {
        periodo: 'mes',
        status: [],
        selectedImovelId: null,
        imoveisLoading: false,
        imoveis: [],
        dateMenu: false,
        dataInicial: moment().startOf('month').format('YYYY-MM-DD'),
        dataFinal: moment().endOf('month').format('YYYY-MM-DD'),
        periodoItems: [
          { text: 'Mais recentes', value: 'recentes' },
          { text: 'Dia', value: 'dia' },
          { text: 'Semana', value: 'semana' },
          { text: 'Mês', value: 'mes' },
          { text: 'Semestre', value: 'semestre' },
          { text: 'Ano', value: 'ano' },
          { text: 'Período customizado', value: 'custom' }
        ],
        statusItems: ['pendente', 'autorizado', 'rejeitado', 'pago']
      }
    },
    mounted () {
      this.syncImovelFromRoute()
      this.loadPagamentos()
      this.$nextTick(() => {
        window.setTimeout(() => {
          this.loadImoveis()
        }, 0)
      })
    },
    watch: {
      periodo () {
        this.loadPagamentos()
      },
      status () {
        this.loadPagamentos()
      },
      '$route.query.imovel_id': function (value) {
        const normalizedValue = normalizeImovelFilterValue(value)
        if (normalizedValue === normalizeImovelFilterValue(this.selectedImovelId)) return
        this.selectedImovelId = normalizedValue
        this.loadPagamentos(normalizedValue)
      }
    },
    computed: {
      filtros () {
        return this.$store.getters['imoveis/filtros']
      },
      imovelItems () {
        const items = this.imoveis.map((item) => ({
          text: item.apelido || `Imóvel #${item.id}`,
          value: String(item.id)
        }))

        return [{ text: 'Todos os imóveis', value: null }].concat(_.orderBy(items, ['text'], ['asc']))
      },
      relatorio () {
        return this.$store.getters['relatorios/pagamentos']
      },
      resumo () {
        return this.relatorio.resumo || {}
      },
      itens () {
        return this.relatorio.itens || []
      },
      statusRows () {
        return _.map(this.relatorio.por_status || {}, (row, key) => ({
          status: this.formatStatus(key),
          total: row.total || 0,
          valor_total: row.valor_total || 0
        }))
      },
      isCustomDateRangeValid () {
        if (!this.dataInicial || !this.dataFinal) return false
        return moment(this.dataInicial).isSameOrBefore(this.dataFinal, 'day')
      },
      periodoTitulo () {
        const periodoAplicado = this.relatorio.periodo_aplicado || {}
        if (periodoAplicado.periodo === 'custom' || (periodoAplicado.data_inicial && periodoAplicado.data_final)) {
          return `${this.formatDate(periodoAplicado.data_inicial)} até ${this.formatDate(periodoAplicado.data_final)}`
        }
        const label = _.find(this.periodoItems, { value: periodoAplicado.periodo || this.periodo })
        return label ? label.text : '-'
      }
    },
    methods: {
      formatDate (value) {
        if (!value) return '-'
        return moment(value).format('DD/MM/YYYY')
      },
      formatCurrency (value) {
        return currencyFormatter.format((value || 0), { code: 'BRL' })
      },
      formatStatus (status) {
        if (!status) return '-'
        return _.startCase(_.toLower(status))
      },
      onCustomDateInput () {
        if (!this.isCustomDateRangeValid || this.periodo !== 'custom') return
        this.loadPagamentos()
      },
      applyCustomDateRange () {
        if (!this.isCustomDateRangeValid) return
        this.dateMenu = false
        if (this.periodo === 'custom') {
          this.loadPagamentos()
        }
      },
      imovelFilter (item, queryText, itemText) {
        return includesNormalizedText(itemText, queryText)
      },
      normalizeSelectedImovel (value) {
        const normalizedValue = normalizeImovelFilterValue(value)

        if (normalizedValue && this.hasImovelOption(normalizedValue)) {
          return normalizedValue
        }

        if (_.isObject(value)) {
          if (value.hasOwnProperty('value')) {
            const normalizedFromValue = normalizeImovelFilterValue(value.value)
            if (normalizedFromValue && this.hasImovelOption(normalizedFromValue)) {
              return normalizedFromValue
            }
          }
          if (value.hasOwnProperty('id')) {
            const normalizedFromId = normalizeImovelFilterValue(value.id)
            if (normalizedFromId && this.hasImovelOption(normalizedFromId)) {
              return normalizedFromId
            }
          }
        }

        const normalizedText = normalizeSearchText(value)
        if (!normalizedText) return null

        const selectedItem = _.find(this.imoveis, (item) => normalizeSearchText(item.apelido || `Imóvel #${item.id}`) === normalizedText)
        return selectedItem ? String(selectedItem.id) : null
      },
      applyImovelSelection (value, forceReload = false) {
        const normalizedValue = this.normalizeSelectedImovel(value)
        const currentValue = normalizeImovelFilterValue(this.selectedImovelId)
        const hasChanged = normalizedValue !== currentValue

        if (hasChanged) {
          this.selectedImovelId = normalizedValue
        }

        this.syncImovelQuery(normalizedValue)

        if (hasChanged || forceReload) {
          this.loadPagamentos(normalizedValue)
        }
      },
      onImovelInput (value) {
        this.applyImovelSelection(value, true)
      },
      hasImovelOption (value) {
        if (value === null) return true
        return this.imovelItems.some(item => item.value === String(value))
      },
      syncImovelFromRoute () {
        this.selectedImovelId = normalizeImovelFilterValue(this.$route.query.imovel_id)
      },
      syncImovelQuery (value) {
        const query = Object.assign({}, this.$route.query)
        const normalizedValue = normalizeImovelFilterValue(value)

        if (normalizedValue) {
          query.imovel_id = normalizedValue
        } else {
          delete query.imovel_id
        }

        if (_.isEqual(query, this.$route.query)) return

        this.$router.replace({ query }).catch(() => {})
      },
      loadImoveis () {
        this.imoveisLoading = true
        this.$store.dispatch('imoveis/loadListaSelect', {}).then((items) => {
          this.imoveis = items
          if (this.selectedImovelId !== null && !this.hasImovelOption(this.selectedImovelId)) {
            this.applyImovelSelection(null, true)
          }
          this.imoveisLoading = false
        }).catch(() => {
          this.imoveis = []
          this.imoveisLoading = false
        })
      },
      buildPayload (imovelIdOverride) {
        const resolvedImovelId = typeof imovelIdOverride === 'undefined'
          ? this.selectedImovelId
          : imovelIdOverride

        return buildRelatorioPagamentosPayload({
          periodo: this.periodo,
          filtros: this.filtros,
          dataInicial: this.dataInicial,
          dataFinal: this.dataFinal,
          status: this.status,
          imovelId: resolvedImovelId
        })
      },
      loadPagamentos (imovelIdOverride) {
        this.$store.dispatch('relatorios/loadPagamentos', this.buildPayload(imovelIdOverride))
      },
      download (tipo) {
        if (this.itens.length < 1) return

        if (tipo === 'pdf') {
          // eslint-disable-next-line new-cap
          const doc = new jsPDF()
          const headers = [['ID', 'Data ref.', 'Vencimento', 'Pagamento', 'Status', 'Descrição', 'Imóvel', 'Valor']]
          const rows = this.itens.map(item => ([
            item.id,
            this.formatDate(item.data_referencia),
            this.formatDate(item.data_vencimento),
            this.formatDate(item.data_pagamento),
            this.formatStatus(item.status),
            item.descricao || '-',
            item.imovel ? item.imovel.apelido : '-',
            this.formatCurrency(item.valor)
          ]))

          doc.text('Relatório de Pagamentos', 14, 15)
          doc.autoTable({
            head: headers,
            body: rows,
            margin: { top: 22 },
            theme: 'grid',
            styles: { fontSize: 8 }
          })
          doc.save(`Relatorio de Pagamentos - ${moment().format('DD.MM.YYYY')}.pdf`)
        }

        if (tipo === 'excel') {
          const header = [['ID', 'Data ref.', 'Vencimento', 'Pagamento', 'Status', 'Descrição', 'Imóvel', 'Solicitante', 'Aprovador', 'Valor']]
          const rows = this.itens.map(item => ([
            item.id,
            this.formatDate(item.data_referencia),
            this.formatDate(item.data_vencimento),
            this.formatDate(item.data_pagamento),
            this.formatStatus(item.status),
            item.descricao || '-',
            item.imovel ? item.imovel.apelido : '-',
            item.solicitante ? item.solicitante.name : '-',
            item.aprovador ? item.aprovador.name : '-',
            this.formatCurrency(item.valor)
          ]))

          XlsxPopulate.fromBlankAsync().then(workbook => {
            const sheetName = 'Relatório de Pagamentos'
            workbook.sheet(0).name(sheetName)
            workbook.sheet(sheetName).cell('A1').value(header)
            workbook.sheet(sheetName).cell('A2').value(rows)
            workbook.sheet(sheetName).range(`A1:J${rows.length + 1}`).style({
              fontFamily: 'Verdana',
              fontSize: 11,
              border: true,
              wrapText: true
            })
            workbook.sheet(sheetName).row(1).style({ bold: true })
            workbook.sheet(sheetName).column('A').width(10)
            workbook.sheet(sheetName).column('B').width(18)
            workbook.sheet(sheetName).column('C').width(18)
            workbook.sheet(sheetName).column('D').width(18)
            workbook.sheet(sheetName).column('E').width(18)
            workbook.sheet(sheetName).column('F').width(60)
            workbook.sheet(sheetName).column('G').width(35)
            workbook.sheet(sheetName).column('H').width(30)
            workbook.sheet(sheetName).column('I').width(30)
            workbook.sheet(sheetName).column('J').width(20)

            return workbook.outputAsync().then((blob) => {
              const fileName = `Relatorio de Pagamentos - ${moment().format('DD.MM.YYYY')}.xlsx`
              if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, fileName)
              } else {
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                document.body.appendChild(a)
                a.href = url
                a.download = fileName
                a.click()
                window.URL.revokeObjectURL(url)
                document.body.removeChild(a)
              }
            })
          })
        }
      }
    }
  }
</script>

<style scoped>
  .resumo-card {
    min-height: 112px;
  }

  .table-responsive {
    width: 100%;
    overflow-x: auto;
  }

  .resumo-status-table,
  .itens-table {
    width: 100%;
    border-collapse: collapse;
  }

  .resumo-status-table th,
  .resumo-status-table td,
  .itens-table th,
  .itens-table td {
    border-bottom: 1px solid #e0e0e0;
    padding: 10px;
    text-align: left;
    font-size: 13px;
  }

  .resumo-status-table th,
  .itens-table th {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
  }

  .filtro-imovel .input-group__selections {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: flex;
    min-width: 0;
  }

  .filtro-imovel .input-group__selections > div {
    max-width: 100%;
    min-width: 0;
  }

  .filtro-imovel .input-group--select__autocomplete {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filtro-imovel .input-group__selections__comma {
    display: inline-block;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }

  .filtro-imovel-opcao-wrap {
    overflow: hidden;
  }

  .filtro-imovel-opcao-title {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filtro-imovel-selecao-title {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 959px) {
    .relatorios-pagamentos-container.container {
      max-width: 100% !important;
      width: 100% !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
</style>



// WEBPACK FOOTER //
// RelatoriosPagamentos.vue?19e86126